import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, ArrowLeft, CheckCircle, AlertTriangle } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

const ResetPasswordPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const invite = searchParams.get("invite") === "1" || token.startsWith("invite_");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    if (!token) {
      toast.error("Lien invalide");
      navigate("/login");
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t("auth.passwordMismatch"));
      return;
    }

    if (password.length < 8) {
      toast.error(t("auth.passwordPolicy"));
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1770586968010-615ece906f63"
          alt="Calm water with lily pads"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/20" />
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/login" className="flex items-center gap-2 mb-12">
            <img src="/logo-kintsugi.png" alt="Kintsugi TCC" className="h-12 w-auto" />
            <span className="font-heading text-xl font-semibold">{t("app.name")}</span>
          </Link>

          {success ? (
            <div data-testid="reset-password-success">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <h1 className="font-heading text-2xl font-semibold">{invite ? t("auth.accountCreated") : t("auth.passwordReset")}</h1>
              </div>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                {invite ? t("auth.accountCreatedDesc") : t("auth.passwordResetDesc")}
              </p>
              <Link to="/login">
                <Button className="w-full gap-2" data-testid="go-to-login-btn">
                  {t("auth.backToLogin")}
                </Button>
              </Link>
            </div>
          ) : (
            <div data-testid="reset-password-form">
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold mb-2" data-testid="reset-password-title">
                {invite ? t("auth.createPassword") : t("auth.newPassword")}
              </h1>
              <p className="text-muted-foreground mb-8">
                {invite ? t("auth.createPasswordDesc") : t("auth.newPasswordDesc")}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">{t("auth.password")}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      data-testid="reset-password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t("auth.confirmPassword")}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                      data-testid="reset-confirm-password-input"
                    />
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    {t("auth.passwordPolicy")}
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || !password || !confirmPassword}
                  data-testid="reset-submit-btn"
                >
                  {loading ? t("common.loading") : invite ? t("auth.createPassword") : t("auth.resetPassword")}
                </Button>
              </form>

              <p className="text-center text-sm text-muted-foreground mt-6">
                <Link to="/login" className="text-primary hover:underline inline-flex items-center gap-1">
                  <ArrowLeft className="w-3 h-3" /> {t("auth.backToLogin")}
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
